// apps/portals/admin/src/nav.ts — buildNav([…manifests], session); ZERO
// hardcoded menu items (DEV-GUIDE-002 §6.4 / REPO-STRUCTURE.md §5.1).
//
// Which modules this portal's MENU shows is decided here, mirroring router.tsx's
// decision about which module ROUTES it serves — the two lists stay in step by
// review, not by machinery (a module can legitimately expose routes without a
// menu entry, e.g. detail screens reached from a table row).
//
// Adding a screen to this portal = adding an entry to that module's manifest.ts.
// This file does not change.
import { buildNav } from "@erp/auth-kit/nav";
import type { ModuleManifest, NavModule } from "@erp/auth-kit/nav";
import type { ModuleId, Session } from "@erp/core-schemas";
// Imported from each module's dedicated ./manifest entry, NOT its ./fe barrel:
// nav is static config with no dependency on the queries/mutations layer, and
// keeping it off /fe means the screen tests that mock /fe don't have to stub
// menu data to render the shell.
import { libAdminNav } from "@erp/module-lib/manifest";
import { cmsAdminNav } from "@erp/module-cms/manifest";
import { finAdminNav } from "@erp/module-fin/manifest";
import { authAdminNav } from "@erp/module-auth/manifest";
// AUDIT was routed here but never listed (audit UX-18): /audit showed no crumb
// and the home menu. The manifest's one item rides audit:read, so a module-
// scoped reader still sees nothing they cannot open.
import { auditAdminNav } from "@erp/module-audit/manifest";
import { GRANTS } from "./grants";

/**
 * Every manifest this portal composes, UNFILTERED. Exported for the shell's
 * `resolveModuleOf` (ERP-DEPLOYMENT-MODEL §13): the built nav drops a module
 * the install does not enable, so it cannot answer "whose route is
 * /library/catalogue/books?" on a CMS-only install — the raw manifests can,
 * and that answer is what puts the unavailable card in place of the screen.
 */
export const ADMIN_MANIFESTS: readonly ModuleManifest[] = [libAdminNav, cmsAdminNav, finAdminNav, authAdminNav, auditAdminNav];

/**
 * The sidebar's modules for this session on this INSTALL. `enabled` is the
 * capabilities manifest's module set (`useCapabilities().modules()`), passed
 * through as buildNav's 4th argument so a manifest whose module the install
 * omits is dropped before the grant filter — whatever the role holds.
 */
export function adminNav(session: Session | null, enabled: readonly ModuleId[]): NavModule[] {
  return buildNav(ADMIN_MANIFESTS, session, GRANTS, enabled);
}
